import type React from "react";
import { useState, useCallback } from "react";

import { ALL, SUB_CATEGORY_DELIMITER, CATEGORY_PARENT_AND_NAME_DELIMITER } from "app/common/services/categoryService";
import SWReactTableWrapper from "app/components/React/Table/SWReactTableWrapper";

import type { ICategoryLeadersTableProps, ICategoryLeadersTableSort } from "./CategoryLeadersTable.types";
import { getColumnsConfig, getTableOptions } from "./CategoryLeadersTableConfig";
import { CategoryLeadersTableTop } from "./CategoryLeadersTableTop/CategoryLeadersTableTop";
import { getTableFilters, getUrlForTableRecord, getTableApiEndpoints } from "./CategoryLeadersTableUtils";

export const CategoryLeadersTable: React.FunctionComponent<ICategoryLeadersTableProps> = (props) => {
    const { services, navParams, metricType, HeaderComponent = CategoryLeadersTableTop } = props;

    const [sort, setSort] = useState<ICategoryLeadersTableSort>({
        field: "Share",
        sortDirection: "desc",
    });

    const tableEndpoints = getTableApiEndpoints(metricType);
    const categoryObject = services.categoryService.categoryQueryParamToCategoryObject(navParams.category);

    const isAllIndustries = categoryObject?.id === ALL;
    const isCustomIndustry = categoryObject?.isCustomCategory;
    const isTopLevelCategory = categoryObject?.children?.length > 0;
    const showIndustryColumn = isAllIndustries || isCustomIndustry || isTopLevelCategory;

    const tableColumns = getColumnsConfig(services.translate, sort, navParams.webSource, showIndustryColumn);
    const tableOptions = getTableOptions(services.translate);
    const tableFilters = getTableFilters(navParams, sort, services, metricType);

    const handleSort = useCallback(
        (sort: ICategoryLeadersTableSort) => {
            setSort(sort);
        },
        [setSort],
    );

    const transformData = useCallback(
        (data) => {
            return {
                itemsCount: data.TotalCount,
                records: data.Data.map((row) => {
                    return {
                        ...row,
                        url: getUrlForTableRecord(row, navParams, services),
                        Category: row.Category
                            ? row.Category.split(SUB_CATEGORY_DELIMITER).join(CATEGORY_PARENT_AND_NAME_DELIMITER)
                            : row.Category,
                    };
                }),
            };
        },
        [navParams, services],
    );

    return (
        <SWReactTableWrapper
            serverApi={tableEndpoints.dataEndpoint}
            tableColumns={tableColumns}
            initialFilters={tableFilters}
            transformData={transformData}
            paginationProps={{
                showPagination: true,
                hasItemsPerPageSelect: false,
            }}
            tableOptions={tableOptions}
            totalRecordsField="itemsCount"
            recordsField="records"
            onSort={handleSort}
            pageIndent={1}
            rowsPerPage={100}
        >
            {(topComponentProps) => (
                <HeaderComponent
                    {...topComponentProps}
                    excelApiEndpoint={tableEndpoints.excelEndpoint}
                    services={services}
                    tableQueryParams={tableFilters}
                    navParams={navParams}
                    sort={sort}
                    metricType={metricType}
                    totalCount={topComponentProps.totalCount}
                />
            )}
        </SWReactTableWrapper>
    );
};
